<template>
    <!-- Database picker (ODOO-3483): log-widget-style list — search row with a
         counter, company badge-chips, grouping by company, "My / All" scope.
         Reused by the DatabaseSelector page (mode="page", dark) and the topbar
         database chip popup (mode="popup", light). -->
    <div :class="['dbp', mode === 'popup' ? 'dbp--popup' : 'dbp--page']">
        <!-- Search row -->
        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
        <div class="dbp-search">
            <i :class="['pi', 'dbp-search-pi', loading ? 'pi-spin pi-spinner' : 'pi-search']"></i>
            <input
                ref="search"
                v-model="searchQuery"
                class="dbp-search-input" v-uiHelp="'topbar.db.search'"
                :placeholder="t('Search databases...')"
            />
            <span class="dbp-counter" v-if="!loading" v-uiHelp="'topbar.db.counter'">{{ filteredDatabases.length }} / {{ scopedDatabases.length }}</span>
            <div class="dbp-scope" v-if="hasScopeToggle">
                <button :class="['dbp-scope-btn', { active: scope === 'my' }]" @click="scope = 'my'" v-uiHelp="{ key: 'topbar.db.scope.my', state: () => (scope === 'my' ? 'on' : 'off') }">
                    {{ t('My') }} <span class="dbp-badge">{{ myCount }}</span>
                </button>
                <button :class="['dbp-scope-btn', { active: scope === 'all' }]" @click="scope = 'all'" v-uiHelp="{ key: 'topbar.db.scope.all', state: () => (scope === 'all' ? 'on' : 'off') }">
                    {{ t('All') }} <span class="dbp-badge">{{ databases.length }}</span>
                </button>
            </div>
            <button
                v-if="adminInfo && isAdmin"
                class="dbp-info-btn" v-uiHelp="'topbar.db.adminInfo'"
                @click="openAdminInfo"
                v-wjTooltip="t('Databases: server / repo / debug info')"
            >
                <i class="pi pi-info-circle"></i>
            </button>
        </div>

        <!-- Company badge-chips (like the log-widget collection/segment chips) -->
        <!-- IE 2026 10 28 / ODOO-5800 (заказ Игоря: фасеты таблиц глобального поиска — на месте, как чипсы баз): вид — общий toggle-chip (assets/styles/toggle-chips.css), тот же у фасетов глобального поиска -->
        <div class="dbp-companies" v-if="companyChips.length > 1">
            <span
                v-for="c in companyChips"
                :key="c.key"
                :class="['dbp-chip', 'toggle-chip', { active: activeTenant === c.key }]" v-uiHelp="{ key: 'topbar.db.company', state: () => (activeTenant === c.key ? 'on' : 'off') }"
                :title="c.key.startsWith('__') ? null : c.key"
                @click="activeTenant = activeTenant === c.key ? null : c.key"
            >● {{ c.title }} {{ c.count }}</span>
        </div>

        <!-- Loading state -->
        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
        <div v-if="loading" class="dbp-state">
            <i class="pi pi-spin pi-spinner dbp-state-pi"></i>
            <p class="dbp-state-text">{{ t('Loading databases...') }}</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="dbp-state">
            <i class="pi pi-exclamation-circle dbp-state-pi dbp-state-pi--err"></i>
            <p class="dbp-state-text dbp-state-text--err">{{ error }}</p>
            <button class="dbp-retry" @click="loadDatabases">{{ t('Retry') }}</button>
        </div>

        <!-- Grouped list -->
        <div v-else class="dbp-list">
            <div v-if="!filteredDatabases.length" class="dbp-state">
                <i class="pi pi-database dbp-state-pi"></i>
                <p class="dbp-state-text">{{ t('No databases found') }}</p>
            </div>

            <div v-for="group in groupedDatabases" :key="group.key" class="dbp-group">
                <div class="dbp-group-header" :title="group.key.startsWith('__') ? null : group.key">
                    {{ group.title }} <span class="dbp-badge">{{ group.items.length }}</span>
                </div>
                <div
                    v-for="db in group.items"
                    :key="db.dbid"
                    :class="['dbp-item', {
                        'is-core': db.is_core,
                        'is-current': currentDbid && currentDbid === db.dbid,
                        'is-selected': selectingDbid === db.dbid,
                        'is-disabled': selectingDbid && selectingDbid !== db.dbid,
                    }]"
                    @click="onSelect(db)" v-uiHelp="{ key: 'topbar.db.row', state: () => (currentDbid && currentDbid === db.dbid ? 'current' : 'other') }"
                >
                    <div class="dbp-item-icon" :style="{ background: db.color || getColor(db) }">
                        <i :class="db.is_core ? 'pi pi-server' : 'pi pi-database'"></i>
                    </div>
                    <div class="dbp-item-center">
                        <div class="dbp-item-name">{{ db.title || db.name }}</div>
                        <div class="dbp-item-meta">
                            <span class="dbp-item-id">{{ db.dbid }}</span>
                            <span class="dbp-item-desc" v-if="db.description">{{ db.description }}</span>
                        </div>
                    </div>
                    <div class="dbp-item-right">
                        <span v-if="currentDbid && currentDbid === db.dbid" class="dbp-status current">
                            <i class="pi pi-check"></i> {{ t('Current', null, 'db') }}<!-- IE 2026 09 24 / ODOO-341407: контекстный ключ словаря (ревизия омонимов) -->
                        </span>
                        <span v-else-if="envMeta(db).badge" class="dbp-env" :class="'dbp-env--' + envMeta(db).cls" v-uiHelp="'topbar.db.env'">{{ envMeta(db).label }}</span>
                        <!-- IE 2026 09 29 / ODOO-342781: роль «только чтение» — звезды базы нет (toggle_favorite_database пишет в ядро) -->
                        <i
                            v-if="!db.is_core && !store.readonly_on()"
                            :class="['dbp-fav', db.is_favorite ? 'pi pi-star-fill is-fav' : 'pi pi-star']" v-uiHelp="{ key: 'topbar.db.fav', state: () => (db.is_favorite ? 'on' : 'off') }"
                            :title="db.is_favorite ? t('Remove from «My»') : t('Add to «My» (favourites)')"
                            @click.stop="toggleFavorite(db)"
                        ></i>
                        <i v-if="selectingDbid === db.dbid" class="pi pi-spin pi-spinner dbp-item-spinner"></i>
                        <i v-else class="pi pi-chevron-right dbp-item-arrow"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Admin info dialog (ODOO-3487) -->
        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
        <Dialog
            v-if="adminInfo"
            v-model:visible="admin.visible"
            modal
            :header="t('Databases — admin info')"
            :style="{ width: '880px', maxWidth: '95vw' }"
        >
            <div v-if="admin.loading" class="dbp-admin-panel">{{ t('Loading catalog info…') }}</div>
            <div v-else-if="admin.error" class="dbp-admin-panel dbp-admin-err">{{ admin.error }}</div>
            <div v-else class="dbp-admin-panel">
                <div class="dbp-admin-versions">
                    <div class="dbp-admin-build">
                        <span><b>{{ t('Frontend build:') }}</b> {{ feVersion || '—' }}</span>
                        <span v-if="feCommit" class="dbp-commit-line" v-html="commitHTML(feCommit, feGit)"></span>
                    </div>
                    <div class="dbp-admin-build">
                        <span><b>{{ t('Backend (current DB):') }}</b> {{ admin.be_running_version || '—' }}</span>
                        <span v-if="admin.be_running_commit" class="dbp-commit-line" v-html="commitHTML(admin.be_running_commit, admin.be_running_git)"></span>
                    </div>
                </div>
                <div class="dbp-admin-tablewrap">
                    <table class="dbp-admin-table">
                        <thead>
                            <tr>
                                <th>{{ t('Database') }}</th>
                                <th>{{ t('PG database') }}</th>
                                <th>{{ t('Server') }}</th>
                                <th>{{ t('Backend') }}</th>
                                <th>{{ t('BE repo @ branch') }}</th>
                                <th>{{ t('FE repo @ branch') }}</th>
                                <th>{{ t('Env') }}</th>
                                <th>{{ t('DB config') }}</th>
                                <th>{{ t('Debug') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in admin.databases"
                                :key="row.dbid"
                                :class="{ 'dbp-admin-current': row.dbid === (admin.current_dbid || currentDbid) }"
                            >
                                <td>
                                    <b>{{ row.title }}</b>
                                    <div class="dbp-admin-muted">{{ row.dbid }}</div>
                                </td>
                                <!-- ODOO-3583: физическое имя PG-базы отдельной колонкой -->
                                <td><code v-if="row.pg_database">{{ row.pg_database }}</code><span v-else>—</span></td>
                                <td>{{ row.node_title || row.node || '—' }}</td>
                                <td>{{ row.be_container || '—' }}</td>
                                <td>
                                    <code v-if="row.be_repo_path">{{ row.be_repo_path }}</code><span v-else>—</span>
                                    <span v-if="row.be_branch"> @ {{ row.be_branch }}</span>
                                    <div v-if="row.dbid === (admin.current_dbid || currentDbid) && row.vscode_deeplink">
                                        <a :href="row.vscode_deeplink">{{ t('Open in VS Code') }}</a>
                                    </div>
                                </td>
                                <td>
                                    <code v-if="row.fe_repo_path">{{ row.fe_repo_path }}</code><span v-else>{{ row.fe_container || '—' }}</span>
                                    <span v-if="row.fe_branch"> @ {{ row.fe_branch }}</span>
                                </td>
                                <td>{{ row.environment || '—' }}</td>
                                <td>
                                    <!-- ODOO-3583: версия конфига В БАЗЕ против env-версии ЕЁ бэкенда
                                         (running_version из каталога); красным при расхождении -->
                                    <div>
                                        <span class="dbp-admin-muted">{{ t('db:') }}</span>
                                        <span :class="{ 'dbp-admin-vmismatch': row.version_mismatch }"
                                        >{{ row.db_config_version || '—' }}</span>
                                    </div>
                                    <div>
                                        <span class="dbp-admin-muted">{{ t('env:') }}</span>
                                        <span :class="{ 'dbp-admin-vmismatch': row.version_mismatch }"
                                        >{{ row.be_env_version || '—' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <span :class="row.debug_active ? 'dbp-admin-on' : 'dbp-admin-off'">
                                        {{ row.debug_active ? t('ON') : t('off') }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <template #footer>
                <button class="dbp-retry" @click="admin.visible = false">{{ t('Close') }}</button>
            </template>
        </Dialog>
    </div>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { t, tn } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n

export default {
    name: 'DatabasePicker',
    props: {
        mode: { type: String, default: 'page' }, // 'page' (dark, full page) | 'popup' (light, topbar)
        currentDbid: { type: String, default: '' },   // mark the DB the user is in
        selectingDbid: { type: String, default: '' }, // spinner + lock while switching
        adminInfo: { type: Boolean, default: false }, // enable the admin info dialog (ODOO-3487)
    },
    emits: ['select', 'loaded'],
    data() {
        return {
            databases: [],
            searchQuery: '',
            activeTenant: null,
            scope: 'all', // 'my' | 'all' — set to 'my' after load when both scopes are meaningful
            loading: true,
            error: null,
            admin: { visible: false, loading: false, error: '', databases: [], be_running_version: '', be_running_commit: '', be_running_git: null, current_dbid: '' },
            feVersion: process.env.VUE_APP_CONFIG_VERSION || '',
            feCommit: process.env.VUE_APP_FE_COMMIT || '',
            feGit: (() => { try { return JSON.parse(process.env.VUE_APP_FE_GIT || '') } catch (e) { return null } })(),
        }
    },
    setup() {
        const store = useMainStore()
        return { store }
    },
    computed: {
        isAdmin() {
            return this.store.checkAccess({ viewAccess: 'access-admin' })
        },
        myCount() {
            return this.databases.filter(db => db.is_my).length
        },
        hasScopeToggle() {
            // "My" only makes sense when it is a real subset (admins see everything;
            // regular users usually have my == all — hide the toggle then)
            return this.myCount > 0 && this.myCount < this.databases.length
        },
        scopedDatabases() {
            if (this.scope === 'my' && this.hasScopeToggle) {
                return this.databases.filter(db => db.is_my)
            }
            return this.databases
        },
        companyChips() {
            const map = {}
            const chips = []
            this.scopedDatabases.forEach(db => {
                const key = db.is_core ? '__core__' : (db.company || '__other__')
                if (!map[key]) {
                    map[key] = { key, title: this.groupTitle(db), count: 0 }
                    chips.push(map[key])
                }
                map[key].count++
            })
            return chips.sort((a, b) => {
                if (a.key === '__core__') return -1
                if (b.key === '__core__') return 1
                return a.title.localeCompare(b.title)
            })
        },
        filteredDatabases() {
            let result = this.scopedDatabases

            if (this.activeTenant) {
                result = result.filter(db =>
                    (db.is_core ? '__core__' : (db.company || '__other__')) === this.activeTenant)
            }

            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase()
                result = result.filter(db =>
                    (db.title || '').toLowerCase().includes(q) ||
                    (db.name || '').toLowerCase().includes(q) ||
                    (db.dbid || '').toLowerCase().includes(q) ||
                    (db.company || '').toLowerCase().includes(q) ||
                    (this.companyTitle(db.company) || '').toLowerCase().includes(q) ||
                    (db.description || '').toLowerCase().includes(q)
                )
            }

            // Sort: core first, then by company title, then by name
            return [...result].sort((a, b) => {
                if (a.is_core && !b.is_core) return -1
                if (!a.is_core && b.is_core) return 1
                const ca = this.companyTitle(a.company), cb = this.companyTitle(b.company)
                if (ca !== cb) return ca.localeCompare(cb)
                return (a.title || a.name || '').localeCompare(b.title || b.name || '')
            })
        },
        groupedDatabases() {
            const byKey = {}
            const groups = []
            for (const db of this.filteredDatabases) {
                const key = db.is_core ? '__core__' : (db.company || '__other__')
                if (!byKey[key]) {
                    byKey[key] = { key, title: this.groupTitle(db), items: [] }
                    groups.push(byKey[key])
                }
                byKey[key].items.push(db)
            }
            return groups
        },
    },
    mounted() {
        this.loadDatabases()
    },
    methods: {
        commitHTML(str, git) {
            // 'hash|ISO-UTC|author|subject' — как в VS Code: хеш · автор · как давно
            // (formatAgoHTML поверх форматтера длительностей AttrGrid) + локальная
            // дата (formatDateHTML utc_to_local, зона пользователя) — «заголовок».
            // git — {dirty:[файлы не в коммите], ahead:N незапушенных}: бейджи +
            // пиктограмма info с tooltip-списком. Старый формат — как есть.
            const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
            const s = String(str || '')
            if (!s.includes('|')) return esc(s)
            const [hash, date, author, ...rest] = s.split('|')
            const subject = rest.join('|')
            let html = `<span class="dbp-commit-hash">${esc(hash)}</span>`
            if (author) html += ` · <b>${esc(author)}</b>`
            if (date) html += ` · ${this.store.formatAgoHTML(date)} (${this.store.formatDateHTML(date, true)})`
            if (subject) html += ` — <span class="dbp-commit-subject">${esc(subject)}</span>`
            // IE 2026 09 23 / ODOO-341407: i18n
            if (git && typeof git === 'object' && ('dirty' in git || 'ahead' in git)) {
                if ((git.dirty || []).length || git.ahead) {
                    const parts = [], tip = []
                    if ((git.dirty || []).length) { parts.push(tn(git.dirty.length, '✎ {n} uncommitted', '✎ {n} uncommitted')); tip.push(t('Uncommitted changes:'), ...git.dirty) }
                    if (git.ahead) { parts.push(tn(git.ahead, '↑ {n} unpushed', '↑ {n} unpushed')); tip.push((tip.length ? String.fromCharCode(10) : '') + t('Unpushed commits: {n}', { n: git.ahead })) }
                    html += ` · <span class="dbp-git-flags" title="${esc(tip.join(String.fromCharCode(10)))}">${parts.join(' · ')} <i class="pi pi-info-circle"></i></span>`
                } else {
                    // чистый чекаут: юзерфрендли-подтверждение вместо пустоты
                    html += ` · <span class="dbp-git-ok">${esc(t('✓ all committed & pushed'))}</span>`   // IE 2026 09 23 / ODOO-341407: ревью i18n — HTML-сущность вне ключа, экранирование при вставке
                }
            }
            return html
        },
        loadDatabases() {   // IE 2026 09 23 / ODOO-341407: i18n
            this.loading = true
            this.error = null
            const payload = JSON.stringify({
                command: 'list_user_databases',
                user: {
                    email: this.store.user?.doc?.email || this.store.user?.email || '',
                    id: this.store.user?.id || '',
                },
            })
            const self = this
            $.ajax({
                url: this.store.url_backend_login,
                type: 'POST',
                data: payload,
                contentType: 'application/json',
                xhrFields: { withCredentials: true },
                success(answer) {
                    if (answer.databases) {
                        self.databases = answer.databases
                        // default scope: my subset when it exists, otherwise everything
                        self.scope = self.hasScopeToggle ? 'my' : 'all'
                        self.$emit('loaded', self.databases)
                    } else if (answer.notification) {
                        self.error = answer.notification.text
                    } else {
                        self.error = t('Unexpected response')
                    }
                    self.loading = false
                },
                error() {
                    self.error = t('Failed to load databases')
                    self.loading = false
                },
            })
        },
        companyTitle(code) {
            if (!code) return ''
            const db = this.databases.find(d => d.company === code && d.company_title)
            return db?.company_title || code.replace('company_', '')
        },
        groupTitle(db) {   // IE 2026 09 23 / ODOO-341407: i18n
            if (db.is_core) return t('Core')
            return db.company ? this.companyTitle(db.company) : t('Other')
        },
        focus() {
            this.$refs.search?.focus()
        },
        onSelect(db) {
            if (this.selectingDbid) return
            this.$emit('select', db)
        },
        // Star/unstar a DB (ODOO-3575). Optimistic flip, then persist to the
        // central USER record so the choice roams between devices. A starred DB
        // joins the "My databases" scope (backend sets is_my |= is_favorite).
        toggleFavorite(db) {
            const next = !db.is_favorite
            db.is_favorite = next
            db.is_my = next || db.is_my
            this.store.executeBackendMethod(this, {
                command: 'toggle_favorite_database',
                params: { dbid: db.dbid },
            }, (answer) => {
                if (!answer || answer.error || typeof answer.is_favorite === 'undefined') {
                    db.is_favorite = !next // revert on failure
                }
            })
        },
        // Colour + badge by database environment (ODOO-3573). Production = green;
        // the rest carry a labelled badge so debug/demo/test/preprod stand out
        // (the old per-dbid hash colour carried no meaning). Environment comes
        // from the backend (list_user_databases → classify_environment).
        envMeta(db) {   // IE 2026 09 23 / ODOO-341407: i18n
            if (db.is_core) {
                return { color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', label: t('Core'), cls: 'core', badge: false }
            }
            const map = {
                production: { color: 'linear-gradient(135deg, #42d392 0%, #2e8b6e 100%)', label: t('Prod'),    cls: 'prod',    badge: false },
                preprod:    { color: 'linear-gradient(135deg, #2997ff 0%, #1a5fb4 100%)', label: t('Preprod'), cls: 'preprod', badge: true },
                demo:       { color: 'linear-gradient(135deg, #f5a623 0%, #c8850f 100%)', label: t('Demo'),    cls: 'demo',    badge: true },
                test:       { color: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)', label: t('Test'),    cls: 'test',    badge: true },
                debug:      { color: 'linear-gradient(135deg, #9aa4b2 0%, #6b7280 100%)', label: t('Debug'),   cls: 'debug',   badge: true },
            }
            return map[db.environment] || map.production
        },
        getColor(db) {
            return this.envMeta(db).color
        },
        // --- Admin info (ODOO-3487) ---
        openAdminInfo() {
            this.admin.visible = true
            this.admin.loading = true
            this.admin.error = ''
            this.store.executeBackendMethod(this, { command: 'databases_admin_info', params: {} }, (answer) => {
                this.admin.loading = false
                if (!answer || answer.error) {
                    this.admin.error = (answer && (answer.message || answer.error)) || t('Failed to load databases info')   // IE 2026 09 23 / ODOO-341407: i18n
                } else {
                    // ODOO-3583: текущая база — первой строкой
                    const _cur = answer.current_dbid || this.currentDbid
                    this.admin.databases = (answer.databases || [])
                        .sort((a, b) => (b.dbid === _cur ? 1 : 0) - (a.dbid === _cur ? 1 : 0))
                    this.admin.be_running_version = answer.be_running_version || ''
                    this.admin.be_running_commit = answer.be_running_commit || ''
                    this.admin.be_running_git = answer.be_running_git || null
                    this.admin.current_dbid = answer.current_dbid || ''
                }
            })
        },
    },
}
</script>

<style scoped>
/* Theme via CSS variables: dark for the full-page selector, light for the
   topbar popup — same markup, log-widget-like visual language. */
.dbp {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.dbp--page {
    --dbp-bg: #2c2c2e;
    --dbp-border: #3a3a3c;
    --dbp-text: #f5f5f7;
    --dbp-muted: #86868b;
    --dbp-faint: #636366;
    --dbp-accent: #2997ff;
    --dbp-hover: #363638;
    --dbp-group-bg: transparent;
}
.dbp--popup {
    --dbp-bg: #ffffff;
    --dbp-border: #dee2e6;
    --dbp-text: #343a40;
    --dbp-muted: #6c757d;
    --dbp-faint: #adb5bd;
    --dbp-accent: #2997ff;
    --dbp-hover: #f5f5f5;
    --dbp-group-bg: #f8f9fa;
}

/* Search row */
.dbp-search {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--dbp-border);
    border-radius: 10px;
    background: var(--dbp-bg);
    margin-bottom: 0.6rem;
}
.dbp-search-pi {
    color: var(--dbp-muted);
}
.dbp-search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: var(--dbp-text);
    font-size: 15px;
    padding: 0.35rem 0;
}
.dbp-search-input::placeholder {
    color: var(--dbp-faint);
}
.dbp-counter {
    color: var(--dbp-muted);
    font-size: 12px;
    white-space: nowrap;
}
.dbp-scope {
    display: flex;
    gap: 2px;
    flex-shrink: 0;
}
.dbp-scope-btn {
    padding: 0.25rem 0.7rem;
    border-radius: 14px;
    border: 1px solid var(--dbp-border);
    background: transparent;
    color: var(--dbp-muted);
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.3rem;
}
.dbp-scope-btn.active {
    background: var(--dbp-accent);
    border-color: var(--dbp-accent);
    color: #fff;
}
.dbp-info-btn {
    border: none;
    background: transparent;
    color: var(--dbp-muted);
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 50%;
}
.dbp-info-btn:hover {
    color: var(--dbp-accent);
}
.dbp-badge {
    background: rgba(128, 128, 128, 0.2);
    padding: 0 0.4rem;
    border-radius: 10px;
    font-size: 11px;
}
.dbp-scope-btn.active .dbp-badge {
    background: rgba(255, 255, 255, 0.25);
}

/* Company badge-chips */
.dbp-companies {
    display: flex;
    flex-wrap: wrap;
    gap: 0.15rem;
    margin-bottom: 0.5rem;
}
/* IE 2026 10 28 / ODOO-5800 (заказ Игоря: фасеты таблиц глобального поиска — на месте, как чипсы баз): вид чипса — общий .toggle-chip (assets/styles/toggle-chips.css); здесь только цвета темы панели */
.dbp-chip {
    --toggle-chip-accent: var(--dbp-accent);
    --toggle-chip-hover: var(--dbp-hover);
}

/* Grouped list */
.dbp-list {
    overflow-y: auto;
    min-height: 0;
    padding-right: 4px;
}
.dbp-list::-webkit-scrollbar {
    width: 6px;
}
.dbp-list::-webkit-scrollbar-thumb {
    background: var(--dbp-border);
    border-radius: 3px;
}
.dbp-group-header {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.25rem;
    color: var(--dbp-muted);
    background: var(--dbp-group-bg);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}
.dbp--page .dbp-group-header {
    background: #1d1d1f; /* page background — keeps sticky headers opaque */
}
.dbp-item {
    display: flex;
    align-items: center;
    /* IE 2026 08 20 / ODOO-336291 (mobile UX): размеры/радиусы как у рейла страниц */
    gap: 10px;
    padding: 6px 10px;
    margin-bottom: 6px;
    background: var(--dbp-bg);
    border: 1px solid var(--dbp-border);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}
.dbp-item:hover {
    background: var(--dbp-hover);
    border-color: var(--dbp-accent);
}
.dbp-item.is-core {
    border-color: #4a3a6e;
}
.dbp-item.is-current {
    border-color: #42d392;
}
.dbp-item.is-selected {
    border-color: var(--dbp-accent);
}
.dbp-item.is-disabled {
    opacity: 0.4;
    pointer-events: none;
}
.dbp-item-icon {
    /* IE 2026 08 20 / ODOO-336291 (mobile UX): плитка как в рейле страниц */
    width: 44px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.dbp-item-icon i {
    font-size: 1.35rem;
    color: #fff;
}
.dbp-item-center {
    flex: 1;
    min-width: 0;
}
.dbp-item-name {
    color: var(--dbp-text);
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.dbp-item-meta {
    display: flex;
    gap: 0.5rem;
    font-size: 12px;
    min-width: 0;
}
.dbp-item-id {
    color: var(--dbp-faint);
    white-space: nowrap;
}
.dbp-item-desc {
    color: var(--dbp-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.dbp-item-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}
.dbp-status {
    font-size: 11px;
    padding: 0.1rem 0.45rem;
    border-radius: 6px;
    background: rgba(128, 128, 128, 0.15);
    color: var(--dbp-muted);
    white-space: nowrap;
}
.dbp-status.current {
    color: #42d392;
}
.dbp-status.demo {
    color: #f5a623;
}
/* Environment badge (ODOO-3573): prod carries no badge (green icon is the cue),
   non-prod environments get a coloured, labelled chip. */
.dbp-env {
    font-size: 11px;
    font-weight: 600;
    padding: 0.1rem 0.45rem;
    border-radius: 6px;
    white-space: nowrap;
    border: 1px solid transparent;
}
.dbp-env--preprod { color: #2997ff; background: rgba(41, 151, 255, 0.14); border-color: rgba(41, 151, 255, 0.35); }
.dbp-env--demo    { color: #f5a623; background: rgba(245, 166, 35, 0.14); border-color: rgba(245, 166, 35, 0.35); }
.dbp-env--test    { color: #a78bfa; background: rgba(167, 139, 250, 0.16); border-color: rgba(167, 139, 250, 0.4); }
.dbp-env--debug   { color: #9aa4b2; background: rgba(154, 164, 178, 0.16); border-color: rgba(154, 164, 178, 0.4); }
/* Favorite star (ODOO-3575): dim by default, gold when starred. */
.dbp-fav {
    font-size: 0.95rem;
    cursor: pointer;
    color: var(--dbp-faint);
    padding: 0.15rem;
    transition: color 0.12s ease, transform 0.12s ease;
}
.dbp-fav:hover { color: #f5c518; transform: scale(1.15); }
.dbp-fav.is-fav { color: #f5c518; }
.dbp-item-arrow {
    color: var(--dbp-faint);
    font-size: 0.8rem;
}
.dbp-item-spinner {
    color: var(--dbp-accent);
}

/* States */
.dbp-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
}
.dbp-state-pi {
    font-size: 2rem;
    color: var(--dbp-muted);
}
.dbp-state-pi--err {
    color: #f09898;
}
.dbp-state-text {
    color: var(--dbp-muted);
    margin-top: 1rem;
    font-size: 15px;
}
.dbp-state-text--err {
    color: #f09898;
}
.dbp-retry {
    margin-top: 1rem;
    padding: 0.5rem 1.5rem;
    background: var(--dbp-accent);
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
}

/* Admin info dialog */
.dbp-admin-panel {
    font-size: 13px;
}
.dbp-admin-err {
    color: #d9534f;
}
.dbp-admin-build {
    display: flex;
    flex-direction: column;
    gap: .1rem;
}
.dbp-commit-line {
    color: var(--text-color-secondary, #6c757d);
    font-size: .92em;
    padding-left: .75rem;
}
.dbp-commit-line .dbp-commit-hash { font-family: monospace; }
.dbp-commit-line .dbp-commit-subject { font-style: italic; }
.dbp-commit-line .dbp-git-flags { color: var(--orange-600, #d97706); cursor: help; white-space: nowrap; }
.dbp-commit-line .dbp-git-ok { color: var(--green-600, #16a34a); white-space: nowrap; }
.dbp-admin-versions {
    display: flex;
    flex-direction: column;
    gap: .6rem;
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
}
.dbp-admin-tablewrap {
    overflow-x: auto;
}
.dbp-admin-table {
    width: 100%;
    border-collapse: collapse;
}
.dbp-admin-table th, .dbp-admin-table td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid #dee2e6;
    vertical-align: top;
}
.dbp-admin-table th {
    font-size: 12px;
    text-transform: uppercase;
    color: #6c757d;
}
.dbp-admin-table code {
    font-size: 12px;
    word-break: break-all;
}
.dbp-admin-current {
    background: rgba(66, 211, 146, 0.08);
}
.dbp-admin-muted {
    color: #6c757d;
    font-size: 11px;
}
.dbp-admin-on {
    color: #d9534f;
    font-weight: 600;
}
.dbp-admin-vmismatch {
    color: #d9534f;
    font-weight: 600;
}
.dbp-admin-off {
    color: #6c757d;
}
</style>
